import { Collapse } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Panel } from 'components/containers';
import LoadingIndicator from 'components/loadingIndicator/loadingIndicator';

import { CryptoPayoutPaymentMethod, CryptoPayoutStatus, CryptoPayoutStatusTransitionHistory } from 'types/cryptoPayout';

import { formatStatusLabel } from './formatStatusLabel';
import LABELS, { KEYS } from './keys';
import {
  CheckIcon,
  CollapseContent,
  ExpandHandleEnd,
  ExpandSectionHandle,
  ExpandSectionIcon,
  FailedIcon,
  Header,
  InactiveCheckIcon,
  Item,
  Row,
  RowGap,
} from './styles';

export interface StatusPayoutInfoProps {
  status: CryptoPayoutStatus;
  statusHistory: CryptoPayoutStatusTransitionHistory[];
  cryptoCurrencyCode: string | null;
  method: CryptoPayoutPaymentMethod | null;
}

export const StatusPayoutInfo = ({ status, statusHistory = [], cryptoCurrencyCode, method }: StatusPayoutInfoProps) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const toggle = () => {
    setIsExpanded(!isExpanded);
  };

  const getStatusIcon = (status: CryptoPayoutStatus) => {
    const isFullfilled = statusHistory.find((s) => s.to === status);
    const isFailed = Boolean(KEYS.FAILED_STATUSES.find((failedStatus) => failedStatus === status));

    if (isFullfilled && isFailed) {
      return <FailedIcon width={16} height={16} />;
    }

    return isFullfilled ? <CheckIcon width={16} height={16} /> : <InactiveCheckIcon width={18} height={18} />;
  };

  const getStatusDate = (status: CryptoPayoutStatus) => {
    const statusEntry = statusHistory.find((s) => s.to === status);
    return statusEntry ? new Date(statusEntry.createdAt).toLocaleString() : <LoadingIndicator scale={0.75} />;
  };

  const getLabel = (rowStatus: CryptoPayoutStatus) => {
    return formatStatusLabel({
      status: rowStatus,
      currentStatus: status,
      cryptoCurrencyCode,
      method,
      t,
    });
  };

  const getSuccessAndPendingItems = () => {
    return KEYS.PREVIEW_STATUSES.map((status) => (
      <Item key={status}>
        <Row>
          <RowGap gap={6}>
            {getStatusIcon(status)}
            {getLabel(status)}
          </RowGap>
          {getStatusDate(status)}
        </Row>
      </Item>
    ));
  };

  const getItemsWithoutPreview = () => {
    return statusHistory.map((statusEntry) => (
      <Item key={statusEntry.to}>
        <Row>
          <RowGap gap={6}>
            {getStatusIcon(statusEntry.to)}
            {getLabel(statusEntry.to)}
          </RowGap>
          {new Date(statusEntry.createdAt).toLocaleString()}
        </Row>
      </Item>
    ));
  };

  const getItems = () => {
    const isFailedStatus = Boolean(KEYS.FAILED_STATUSES.find((failedStatus) => failedStatus === status));
    if (isFailedStatus) {
      return getItemsWithoutPreview();
    }
    return getSuccessAndPendingItems();
  };

  return (
    <Panel>
      <ExpandSectionHandle flat onClick={toggle}>
        <Header>{t(LABELS.HEADER)}</Header>
        <ExpandHandleEnd>
          {getLabel(status)}
          <ExpandSectionIcon isExpanded={isExpanded} />
        </ExpandHandleEnd>
      </ExpandSectionHandle>
      <Collapse in={isExpanded}>
        <CollapseContent>{getItems()}</CollapseContent>
      </Collapse>
    </Panel>
  );
};

export default StatusPayoutInfo;
